import {
  ProjectDataLoadOptions,
  ProjectDataNode,
  ProjectDataSnapshotCache,
  ProjectDataRepositoryOptions,
  ProjectDataRepositoryStatus,
  ProjectDataUnsubscribe,
} from './project-data-repository.types';

const DEFAULT_PAGE_SIZE = 10_000;
const MAX_SNAPSHOT_PAGE_SIZE = 100_000;
const RANDOM_ID_RADIX = 36;
const RANDOM_ID_PREFIX_LENGTH = 2;
const EMPTY_CHILDREN: readonly string[] = [];

type Listener = () => void;

/**
 * Project-scoped view of the Connect `GET /files/fs/snapshot` feed, shared by
 * React and Solid consumers through the `/react` and `/solid` adapters.
 *
 * Invariants that keep a refresh consistent:
 *
 * - Readers see one generation. Cold reads and the live tree stay on
 *   `activeCacheGeneration`. An in-flight `loadingGeneration` is never
 *   treated as a complete snapshot.
 * - The hot set is every observed parent, every in-memory node, and every
 *   `subscribeNode` id. `completeGeneration` applies that whole set from one
 *   snapshot (cache, or `loadingNodes` if the cache cannot be read).
 * - A children list is complete or absent. `ensureNode` and a listened node
 *   under a cold parent never create one.
 * - A cache flip does not mutate the live tree mid-refresh. Pages stage in
 *   `loadingNodes`; the next `completeGeneration` is the only swap.
 * - Abort belongs to the refresh that started it. A superseded run must not
 *   inspect the replacement controller or delete the newer generation.
 */
export class ProjectDataRepository {
  private readonly projectId: string;
  private readonly rootIdValue: string;
  private readonly projectKey: string;
  private readonly source: ProjectDataRepositoryOptions['source'];
  private readonly cache?: ProjectDataSnapshotCache;
  private readonly pageSize: number;

  private readonly nodesById = new Map<string, ProjectDataNode>();
  private readonly childrenByParentId = new Map<string, readonly string[]>();
  private readonly loadingNodes = new Map<string, ProjectDataNode>();
  private readonly hotParentIds = new Set<string>();
  private readonly nodeListeners = new Map<string, Set<Listener>>();
  private readonly childrenListeners = new Map<string, Set<Listener>>();
  private readonly statusListeners = new Set<Listener>();

  private cacheAvailable: boolean;
  private activeCacheGeneration?: string;
  private loadingGeneration?: string;
  private nextPage?: string;
  private abortController?: AbortController;
  private searchAbortController?: AbortController;
  private searchGeneration = 0;
  private disposed = false;
  private statusValue: ProjectDataRepositoryStatus = {
    phase: 'idle',
    loadedCount: 0,
    hasNextPage: false,
  };

  constructor(options: ProjectDataRepositoryOptions) {
    this.projectId = options.projectId;
    this.rootIdValue = options.rootId;
    this.projectKey = `${options.region}:${options.projectId}`;
    this.source = options.source;
    this.cache = options.cache;
    this.cacheAvailable = options.cache !== undefined;
    this.pageSize = Math.min(
      Math.max(options.pageSize ?? DEFAULT_PAGE_SIZE, 1),
      MAX_SNAPSHOT_PAGE_SIZE
    );
    this.hotParentIds.add(this.rootIdValue);
  }

  public get rootId(): string {
    return this.rootIdValue;
  }

  public get status(): ProjectDataRepositoryStatus {
    return this.statusValue;
  }

  public getNode(id: string): ProjectDataNode | undefined {
    return this.nodesById.get(id);
  }

  public getChildren(parentId: string): readonly string[] {
    return this.childrenByParentId.get(parentId) ?? EMPTY_CHILDREN;
  }

  public async initialize(options: ProjectDataLoadOptions = {}): Promise<void> {
    this.assertActive();
    await this.hydrateFromCache();
    await this.refresh(options);
  }

  public async refresh(options: ProjectDataLoadOptions = {}): Promise<void> {
    this.assertActive();
    this.abortController?.abort();
    if (!this.cacheAvailable && this.activeCacheGeneration === undefined) {
      this.nodesById.clear();
      this.childrenByParentId.clear();
      this.nodeListeners.forEach((listeners) => this.notify(listeners));
      this.childrenListeners.forEach((listeners) => this.notify(listeners));
    }
    const controller = new AbortController();
    this.abortController = controller;
    this.loadingGeneration = this.createGeneration();
    this.loadingNodes.clear();
    this.nextPage = undefined;
    this.setStatus({
      phase: 'loading',
      loadedCount: 0,
      hasNextPage: true,
    });

    try {
      await this.fetchNextPage();
      while (options.allPages && this.nextPage && !this.disposed) {
        await this.fetchNextPage();
      }
    } catch (error: unknown) {
      await this.failLoading(error, controller);
    }
  }

  public async loadNextPage(): Promise<void> {
    this.assertActive();
    if (!this.loadingGeneration) {
      await this.refresh();
      return;
    }
    if (!this.nextPage) return;

    const controller = this.abortController;
    try {
      await this.fetchNextPage();
    } catch (error: unknown) {
      await this.failLoading(error, controller);
    }
  }

  public async ensureNode(nodeId: string): Promise<void> {
    this.assertActive();
    if (this.nodesById.has(nodeId)) return;

    const generation = this.readableGeneration();
    if (!generation || !this.cache) return;

    const node = await this.tryCacheOperation(() =>
      this.cache?.readNode(this.projectKey, generation, nodeId)
    );
    if (this.disposed) return;
    if (node && !node.isDeleted) this.putNode(node);
  }

  public async ensureChildren(parentId: string): Promise<void> {
    this.assertActive();
    this.hotParentIds.add(parentId);

    const generation = this.readableGeneration();
    if (!generation || !this.cache) return;

    const children = await this.tryCacheOperation(() =>
      this.cache?.readChildren(this.projectKey, generation, parentId)
    );
    if (this.disposed || children === undefined) return;
    this.replaceChildren(parentId, children);
  }

  public async search(
    query: string,
    signal?: AbortSignal
  ): Promise<readonly ProjectDataNode[]> {
    this.assertActive();
    const normalizedQuery = query.trim();
    if (!normalizedQuery) return [];

    this.searchAbortController?.abort();
    const controller = new AbortController();
    this.searchAbortController = controller;
    const generation = ++this.searchGeneration;
    const abortSearch = (): void => controller.abort();
    signal?.addEventListener('abort', abortSearch, { once: true });
    if (signal?.aborted) controller.abort();

    const results: ProjectDataNode[] = [];
    let next: string | undefined;

    try {
      do {
        this.throwIfSearchInactive(controller, generation);
        const page = await this.source.fetchPage({
          projectId: this.projectId,
          maxItems: this.pageSize,
          objectNames: normalizedQuery,
          next,
          signal: controller.signal,
        });
        this.throwIfSearchInactive(controller, generation);
        results.push(...page.items.filter((item) => !item.isDeleted));
        next = page.next;
      } while (next);

      return results;
    } finally {
      signal?.removeEventListener('abort', abortSearch);
      if (this.searchAbortController === controller) {
        this.searchAbortController = undefined;
      }
    }
  }

  public subscribeStatus(listener: Listener): ProjectDataUnsubscribe {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  public subscribeNode(
    nodeId: string,
    listener: Listener
  ): ProjectDataUnsubscribe {
    const listeners = this.getOrCreateListeners(this.nodeListeners, nodeId);
    listeners.add(listener);
    void this.ensureNode(nodeId);
    return () => this.removeListener(this.nodeListeners, nodeId, listener);
  }

  public subscribeChildren(
    parentId: string,
    listener: Listener
  ): ProjectDataUnsubscribe {
    const listeners = this.getOrCreateListeners(
      this.childrenListeners,
      parentId
    );
    listeners.add(listener);
    void this.ensureChildren(parentId);
    return () =>
      this.removeListener(this.childrenListeners, parentId, listener);
  }

  public dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.abortController?.abort();
    this.searchAbortController?.abort();
    this.nodeListeners.clear();
    this.childrenListeners.clear();
    this.statusListeners.clear();
    this.cache?.dispose();
    this.statusValue = {
      phase: 'disposed',
      loadedCount: this.statusValue.loadedCount,
      hasNextPage: false,
    };
  }

  private async hydrateFromCache(): Promise<void> {
    if (!this.cacheAvailable) return;
    this.setStatus({
      phase: 'hydrating',
      loadedCount: 0,
      hasNextPage: false,
    });

    const metadata = await this.tryCacheOperation(() =>
      this.cache?.getMetadata(this.projectKey)
    );
    this.activeCacheGeneration = metadata?.activeGeneration;
    if (!this.activeCacheGeneration) return;

    const children = await this.tryCacheOperation(() =>
      this.cache?.readChildren(
        this.projectKey,
        this.activeCacheGeneration!,
        this.rootIdValue
      )
    );
    if (children !== undefined)
      this.replaceChildren(this.rootIdValue, children);
  }

  private async fetchNextPage(): Promise<void> {
    const generation = this.loadingGeneration;
    const controller = this.abortController;
    if (!generation || !controller) return;

    const page = await this.source.fetchPage({
      projectId: this.projectId,
      maxItems: this.pageSize,
      next: this.nextPage,
      signal: controller.signal,
    });
    if (!this.isCurrentLoad(generation, controller)) return;

    await this.tryCacheOperation(() =>
      this.cache?.writePage(this.projectKey, generation, page.items)
    );
    if (!this.isCurrentLoad(generation, controller)) return;

    this.ingestPage(page.items);
    this.nextPage = page.next;
    const loadedCount = this.statusValue.loadedCount + page.items.length;

    if (page.next) {
      this.setStatus({
        phase: 'partial',
        loadedCount,
        hasNextPage: true,
      });
      return;
    }

    const completed = await this.completeGeneration(generation);
    if (!completed) return;
    this.setStatus({
      phase: 'complete',
      loadedCount,
      hasNextPage: false,
    });
  }

  private ingestPage(items: readonly ProjectDataNode[]): void {
    for (const item of items) {
      this.loadingNodes.set(item.id, item);
    }

    const visibleItems = items.filter((item) => !item.isDeleted);

    // A later refresh writes into the loading generation only. The live tree
    // keeps the last committed snapshot until completeGeneration swaps it —
    // including when quota/private-mode flips cacheAvailable mid-refresh.
    // Upserting here would mix new adds with stale removals.
    if (this.activeCacheGeneration !== undefined) {
      return;
    }

    if (!this.cacheAvailable) {
      this.upsertHotNodes(visibleItems);
      return;
    }

    const hotParentItems: ProjectDataNode[] = [];
    const listenedItems: ProjectDataNode[] = [];
    for (const item of visibleItems) {
      if (this.hotParentIds.has(item.parentId)) hotParentItems.push(item);
      else if (this.nodeListeners.has(item.id)) listenedItems.push(item);
    }

    this.upsertHotNodes(hotParentItems);
    listenedItems.forEach((item) => this.putNode(item));
  }

  private putNode(node: ProjectDataNode): void {
    const previous = this.nodesById.get(node.id);
    this.nodesById.set(node.id, node);
    if (previous !== node) {
      this.notify(this.nodeListeners.get(node.id));
    }
  }

  private upsertHotNodes(items: readonly ProjectDataNode[]): void {
    const changedNodeIds = new Set<string>();
    const changedParentIds = new Set<string>();

    for (const item of items) {
      const previous = this.nodesById.get(item.id);
      this.nodesById.set(item.id, item);
      if (previous !== item) {
        changedNodeIds.add(item.id);
        changedParentIds.add(item.parentId);
      }

      const currentChildren = this.childrenByParentId.get(item.parentId) ?? [];
      if (!currentChildren.includes(item.id)) {
        this.childrenByParentId.set(item.parentId, [
          ...currentChildren,
          item.id,
        ]);
        changedParentIds.add(item.parentId);
      }
    }

    changedNodeIds.forEach((id) => this.notify(this.nodeListeners.get(id)));
    changedParentIds.forEach((id) =>
      this.notify(this.childrenListeners.get(id))
    );
  }

  private replaceChildren(
    parentId: string,
    children: readonly ProjectDataNode[]
  ): void {
    const previousIds = this.childrenByParentId.get(parentId) ?? [];
    let nodesChanged = false;
    const nextIds = children
      .filter((node) => !node.isDeleted)
      .map((node) => node.id);

    const nextIdSet = new Set(nextIds);
    for (const previousId of previousIds) {
      if (!nextIdSet.has(previousId)) {
        const current = this.nodesById.get(previousId);
        if (current !== undefined && current.parentId !== parentId) {
          continue;
        }
        this.nodesById.delete(previousId);
        this.notify(this.nodeListeners.get(previousId));
        nodesChanged = true;
      }
    }
    for (const node of children) {
      if (!node.isDeleted) {
        nodesChanged ||= this.nodesById.get(node.id) !== node;
        this.nodesById.set(node.id, node);
      }
    }
    if (!this.sameIds(previousIds, nextIds)) {
      this.childrenByParentId.set(parentId, nextIds);
      this.notify(this.childrenListeners.get(parentId));
    } else if (nodesChanged) {
      this.notify(this.childrenListeners.get(parentId));
    }
    nextIds.forEach((id) => this.notify(this.nodeListeners.get(id)));
  }

  private async completeGeneration(generation: string): Promise<boolean> {
    if (!this.isCurrentLoad(generation)) return false;

    const hadCommittedSnapshot = this.activeCacheGeneration !== undefined;
    const committed = await this.commitIfPossible(generation);
    if (!this.isCurrentLoad(generation)) return false;

    const appliedFromCache = await this.applyHotSetFromCache(generation);
    if (!this.isCurrentLoad(generation)) return false;
    if (appliedFromCache) {
      this.activeCacheGeneration = generation;
      if (this.cacheAvailable) {
        void this.tryCacheOperation(() =>
          this.cache?.deleteInactiveGenerations(this.projectKey, generation)
        );
      }
      this.finishLoading();
      return true;
    }

    if (committed || !hadCommittedSnapshot) this.applyHotSetFromMemory();
    else this.applyFullSnapshotFromMemory();
    this.activeCacheGeneration = committed ? generation : undefined;
    this.finishLoading();
    return true;
  }

  private isCurrentLoad(
    generation: string,
    controller?: AbortController
  ): boolean {
    return (
      !this.disposed &&
      this.loadingGeneration === generation &&
      !controller?.signal.aborted
    );
  }

  private async commitIfPossible(generation: string): Promise<boolean> {
    if (!this.cacheAvailable || !this.cache) return false;
    try {
      await this.cache.commitGeneration(this.projectKey, generation);
      return true;
    } catch {
      this.cacheAvailable = false;
      return false;
    }
  }

  /**
   * Read the whole hot set first, then write it. A thrown cache read aborts
   * before any live-tree mutation so two generations never mix; the caller
   * then applies `loadingNodes` instead. A missing node is not a failure —
   * `readNode` returns undefined and the id is dropped.
   */
  private async applyHotSetFromCache(generation: string): Promise<boolean> {
    if (!this.cache || !this.cacheAvailable || this.disposed) return false;

    const parentReads = await Promise.all(
      [...this.hotParentIds].map(async (parentId) => ({
        parentId,
        children: await this.tryCacheOperation(() =>
          this.cache?.readChildren(this.projectKey, generation, parentId)
        ),
      }))
    );
    if (
      this.disposed ||
      !this.cacheAvailable ||
      !this.isCurrentLoad(generation)
    ) {
      return false;
    }
    if (parentReads.some((read) => read.children === undefined)) return false;

    const visibleChildIds = new Set<string>();
    for (const { children } of parentReads) {
      children?.forEach((child) => {
        if (!child.isDeleted) visibleChildIds.add(child.id);
      });
    }

    const nodeReads = await Promise.all(
      this.pendingHotNodeIds(visibleChildIds).map(async (nodeId) => ({
        nodeId,
        node: await this.tryCacheOperation(() =>
          this.cache?.readNode(this.projectKey, generation, nodeId)
        ),
      }))
    );
    if (
      this.disposed ||
      !this.cacheAvailable ||
      !this.isCurrentLoad(generation)
    ) {
      return false;
    }

    for (const { parentId, children } of parentReads) {
      if (children !== undefined) this.replaceChildren(parentId, children);
    }
    for (const { nodeId, node } of nodeReads) {
      if (node && !node.isDeleted) this.putNode(node);
      else this.dropNode(nodeId);
    }
    return true;
  }

  private applyHotSetFromMemory(): void {
    const visibleChildIds = new Set<string>();
    for (const parentId of this.hotParentIds) {
      const children = this.loadingChildren(parentId);
      this.replaceChildren(parentId, children);
      children.forEach((child) => {
        if (!child.isDeleted) visibleChildIds.add(child.id);
      });
    }

    for (const nodeId of this.pendingHotNodeIds(visibleChildIds)) {
      const node = this.loadingNodes.get(nodeId);
      if (node && !node.isDeleted) this.putNode(node);
      else this.dropNode(nodeId);
    }
  }

  /**
   * Cache never accepted this generation, so cold reads cannot recover it.
   * Materialize every staged parent; the in-memory tree is the snapshot.
   */
  private applyFullSnapshotFromMemory(): void {
    const parentIds = new Set<string>(this.hotParentIds);
    this.childrenByParentId.forEach((_value, parentId) => {
      parentIds.add(parentId);
    });
    this.loadingNodes.forEach((node) => parentIds.add(node.parentId));

    const visibleChildIds = new Set<string>();
    for (const parentId of parentIds) {
      const children = this.loadingChildren(parentId);
      this.replaceChildren(parentId, children);
      children.forEach((child) => {
        if (!child.isDeleted) visibleChildIds.add(child.id);
      });
    }

    for (const nodeId of this.pendingHotNodeIds(visibleChildIds)) {
      const node = this.loadingNodes.get(nodeId);
      if (node && !node.isDeleted) this.putNode(node);
      else this.dropNode(nodeId);
    }
  }

  private loadingChildren(parentId: string): ProjectDataNode[] {
    const children: ProjectDataNode[] = [];
    this.loadingNodes.forEach((node) => {
      if (node.parentId === parentId) children.push(node);
    });
    return children;
  }

  private pendingHotNodeIds(visibleChildIds: ReadonlySet<string>): string[] {
    const pending = new Set<string>(this.nodeListeners.keys());
    this.nodesById.forEach((_node, nodeId) => pending.add(nodeId));
    visibleChildIds.forEach((nodeId) => pending.delete(nodeId));
    return [...pending];
  }

  private dropNode(nodeId: string): void {
    const previous = this.nodesById.get(nodeId);
    if (!this.nodesById.delete(nodeId)) return;
    this.notify(this.nodeListeners.get(nodeId));
    if (!previous) return;

    const siblingIds = this.childrenByParentId.get(previous.parentId);
    if (!siblingIds?.includes(nodeId)) return;
    this.childrenByParentId.set(
      previous.parentId,
      siblingIds.filter((siblingId) => siblingId !== nodeId)
    );
    this.notify(this.childrenListeners.get(previous.parentId));
  }

  private finishLoading(): void {
    this.loadingGeneration = undefined;
    this.nextPage = undefined;
    this.loadingNodes.clear();
  }

  /**
   * A refresh that a newer one superseded must stay silent: its controller is
   * the aborted one, so checking the live `abortController` would report the
   * abort as a failure and discard the generation the newer refresh is filling.
   */
  private async failLoading(
    error: unknown,
    controller?: AbortController
  ): Promise<void> {
    if (controller?.signal.aborted || this.disposed) return;

    const normalizedError =
      error instanceof Error ? error : new Error(String(error));
    const hasNextPage = this.nextPage !== undefined;
    await this.removeIncompleteGeneration();
    this.setStatus({
      phase: 'error',
      loadedCount: this.statusValue.loadedCount,
      hasNextPage,
      error: normalizedError,
    });
    throw normalizedError;
  }

  private async removeIncompleteGeneration(): Promise<void> {
    const generation = this.loadingGeneration;
    this.finishLoading();
    if (generation && this.cacheAvailable) {
      await this.tryCacheOperation(() =>
        this.cache?.deleteGeneration(this.projectKey, generation)
      );
    }
  }

  /**
   * Cold reads use the last committed snapshot only. A loading generation is
   * still streaming, so treating it as readable would publish a partial
   * children list as if it were complete.
   */
  private readableGeneration(): string | undefined {
    return this.activeCacheGeneration;
  }

  private setStatus(status: ProjectDataRepositoryStatus): void {
    this.statusValue = status;
    this.notify(this.statusListeners);
  }

  private notify(listeners?: ReadonlySet<Listener>): void {
    listeners?.forEach((listener) => listener());
  }

  private getOrCreateListeners(
    map: Map<string, Set<Listener>>,
    key: string
  ): Set<Listener> {
    const existing = map.get(key);
    if (existing) return existing;
    const listeners = new Set<Listener>();
    map.set(key, listeners);
    return listeners;
  }

  private removeListener(
    map: Map<string, Set<Listener>>,
    key: string,
    listener: Listener
  ): void {
    const listeners = map.get(key);
    listeners?.delete(listener);
    if (listeners?.size === 0) map.delete(key);
  }

  private async tryCacheOperation<T>(
    operation: () => Promise<T> | undefined
  ): Promise<T | undefined> {
    try {
      return await operation();
    } catch {
      this.cacheAvailable = false;
      return undefined;
    }
  }

  private createGeneration(): string {
    return typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : // eslint-disable-next-line sonarjs/pseudo-random -- local fallback id only, not security-sensitive
        `${Date.now()}-${Math.random()
          .toString(RANDOM_ID_RADIX)
          .slice(RANDOM_ID_PREFIX_LENGTH)}`;
  }

  private sameIds(left: readonly string[], right: readonly string[]): boolean {
    return (
      left.length === right.length &&
      left.every((value, index) => value === right[index])
    );
  }

  private assertActive(): void {
    if (this.disposed) {
      throw new Error('ProjectDataRepository has been disposed');
    }
  }

  private throwIfSearchInactive(
    controller: AbortController,
    generation: number
  ): void {
    if (
      controller.signal.aborted ||
      this.disposed ||
      generation !== this.searchGeneration
    ) {
      throw new DOMException('Project data search was aborted', 'AbortError');
    }
  }
}
